import React from 'react';
import { addDays, format, parseISO } from 'date-fns';
import { styled, Typography } from '@mui/material';
import { IntervalFieldMode, IntervalOption } from 'api/report/types';
import { Checkbox } from 'components/Checkbox';
import { Field } from 'components/Widget/Form/Form';
import { DateTimeField } from './DateTimeField';
import { ClockChangeOptions } from './ClockChangeOptions';
import { ScrollHint } from 'components/ScrollHint/ScrollHint';
import { useScrollOverflow } from 'components/ScrollHint/useScrollOverflow';
import Selector from 'components/interface/selector/Selector';
import Chip from 'components/interface/Chip/Chip';
import TextField from 'components/interface/textField/TextField';
import {
    changeIntervalEntryKind,
    changeIntervalEntryTime,
} from 'report/logic/interval/editing';
import { formatIntervalKind } from './formatting';
import { EntryFieldsProps } from '../types';

const EditorBody = styled('div')({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
});

const FieldsViewport = styled('div')({
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    scrollbarWidth: 'thin',
});

const EditorFields = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 2, 2),
}));

const LocationOption = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: '100%',
}));

const optionLabel = (option: IntervalOption): string =>
    [option.group?.name, option.name].filter(Boolean).join(' · ');

const locationLabel = (option: IntervalOption): string =>
    option.group?.name ? `${option.name} (${option.group.name})` : option.name;

export function EntryFields({
    autoFocus = false,
    draft,
    validation,
    entryKinds,
    timeZone,
    disabled,
    onChange,
}: EntryFieldsProps) {
    const { parentRef, contentRef, hasMoreBelow } = useScrollOverflow();
    const lastDate = format(
        addDays(parseISO(draft.reportingDate), 1),
        'yyyy-MM-dd'
    );
    const config = entryKinds.find(config => config.kind === draft.kind);
    const locationOptions = React.useMemo(
        () =>
            [...(config?.location.options || [])].sort(
                (a, b) =>
                    a.name.localeCompare(b.name, 'sv') ||
                    (a.group?.name || '').localeCompare(
                        b.group?.name || '',
                        'sv'
                    )
            ),
        [config?.location.options]
    );
    // Keep the saved category visible if its kind is no longer in the template.
    const selectedKind = config || {
        kind: draft.kind,
        label: formatIntervalKind(draft.kind, entryKinds),
    };
    const { errors, timeOptions } = validation;
    const selectedTimeOption =
        timeOptions.find(
            option =>
                option.startAt === draft.startAt && option.endAt === draft.endAt
        ) ?? null;
    const showBreaks =
        config?.allowBreaks ||
        Number(draft.breakMinutes) > 0 ||
        !!errors.breakMinutes;
    const showLocation =
        draft.location ||
        (config && config.location.mode !== IntervalFieldMode.DISABLED);
    const showReason =
        draft.reason ||
        (config && config.reason.mode !== IntervalFieldMode.DISABLED);
    const showDistance =
        draft.distanceKm !== '' ||
        (config && config.distanceMode !== IntervalFieldMode.DISABLED);
    const showSickness = config?.allowSickness || draft.isSick;
    const handleTimeChange = (
        field: 'startDate' | 'startTime' | 'endDate' | 'endTime',
        value: string
    ) => {
        onChange(previous =>
            changeIntervalEntryTime(previous, field, value, timeZone)
        );
    };

    return (
        <EditorBody>
            <FieldsViewport ref={parentRef}>
                <EditorFields ref={contentRef}>
                    <Selector
                        label='Kategori'
                        options={entryKinds}
                        selected={selectedKind}
                        getOptionLabel={option => option.label}
                        compareFunc={(a, b) => a.kind === b?.kind}
                        onChange={option => {
                            const nextConfig = entryKinds.find(
                                config => config.kind === option?.kind
                            );
                            if (nextConfig) {
                                onChange(prev =>
                                    changeIntervalEntryKind(prev, nextConfig)
                                );
                            }
                        }}
                        error={!!errors.kind}
                        helperText={errors.kind}
                        disabled={disabled}
                        fullWidth
                    />
                    <DateTimeField
                        label='Startdatum och tid'
                        date={draft.startDate}
                        time={draft.startTime}
                        minDate={draft.reportingDate}
                        maxDate={lastDate}
                        onDateChange={startDate =>
                            handleTimeChange('startDate', startDate)
                        }
                        onTimeChange={startTime =>
                            handleTimeChange('startTime', startTime)
                        }
                        autoFocus={autoFocus}
                        error={errors.startDate || errors.startTime}
                        disabled={disabled}
                    />
                    <DateTimeField
                        label='Slutdatum och tid'
                        date={draft.endDate}
                        time={draft.endTime}
                        minDate={draft.reportingDate}
                        maxDate={lastDate}
                        onDateChange={endDate =>
                            handleTimeChange('endDate', endDate)
                        }
                        onTimeChange={endTime =>
                            handleTimeChange('endTime', endTime)
                        }
                        error={errors.endDate || errors.endTime}
                        disabled={disabled}
                    />
                    {timeOptions.length > 1 && (
                        <ClockChangeOptions
                            options={timeOptions}
                            selected={selectedTimeOption}
                            disabled={disabled}
                            error={errors.timeRange}
                            onChange={option => {
                                if (option === selectedTimeOption) {
                                    return;
                                }
                                onChange(prev => ({
                                    ...prev,
                                    startAt: option.startAt,
                                    endAt: option.endAt,
                                }));
                            }}
                        />
                    )}
                    {showBreaks && (
                        <TextField
                            label='Rast'
                            value={draft.breakMinutes}
                            onChange={breakMinutes =>
                                onChange(prev => ({
                                    ...prev,
                                    breakMinutes,
                                }))
                            }
                            type='number'
                            min={0}
                            step={1}
                            error={!!errors.breakMinutes}
                            helperText={errors.breakMinutes}
                            endAdornment={
                                <Typography variant='caption'>min</Typography>
                            }
                            disabled={disabled}
                            fullWidth
                        />
                    )}
                    {showLocation && (
                        <Selector
                            label={config?.location.label || 'Plats'}
                            options={locationOptions}
                            selected={draft.location}
                            getOptionLabel={locationLabel}
                            renderOption={option => (
                                <LocationOption>
                                    <Typography
                                        variant='body2'
                                        fontWeight={500}>
                                        {option.name}
                                    </Typography>
                                    {option.group?.name && (
                                        <Chip
                                            label={option.group.name}
                                            color='grey'
                                            size='small'
                                            sx={{ flexShrink: 0 }}
                                        />
                                    )}
                                </LocationOption>
                            )}
                            compareFunc={(a, b) => a.uuid === b?.uuid}
                            onChange={location =>
                                onChange(prev => ({
                                    ...prev,
                                    location,
                                }))
                            }
                            required={
                                config?.location.mode ===
                                IntervalFieldMode.REQUIRED
                            }
                            error={!!errors.location}
                            helperText={errors.location}
                            disabled={disabled}
                            fullWidth
                        />
                    )}
                    {showReason && (
                        <Selector
                            label={config?.reason.label || 'Orsak'}
                            options={config?.reason.options || []}
                            selected={draft.reason}
                            getOptionLabel={optionLabel}
                            compareFunc={(a, b) => a.uuid === b?.uuid}
                            onChange={reason =>
                                onChange(prev => ({ ...prev, reason }))
                            }
                            required={
                                config?.reason.mode ===
                                IntervalFieldMode.REQUIRED
                            }
                            error={!!errors.reason}
                            helperText={errors.reason}
                            disabled={disabled}
                            fullWidth
                        />
                    )}
                    {showDistance && (
                        <TextField
                            label='Körsträcka'
                            value={draft.distanceKm}
                            onChange={distanceKm =>
                                onChange(prev => ({
                                    ...prev,
                                    distanceKm,
                                }))
                            }
                            type='number'
                            min={0}
                            step={1}
                            required={
                                config?.distanceMode ===
                                IntervalFieldMode.REQUIRED
                            }
                            error={!!errors.distanceKm}
                            helperText={errors.distanceKm}
                            endAdornment={
                                <Typography variant='caption'>km</Typography>
                            }
                            disabled={disabled}
                            fullWidth
                        />
                    )}
                    <TextField
                        label='Anteckning'
                        value={draft.note}
                        onChange={note => onChange(prev => ({ ...prev, note }))}
                        disabled={disabled}
                        fullWidth
                        inputSx={{ fontSize: 14 }}
                    />
                    {showSickness && (
                        <Field label='Sjukfrånvaro'>
                            <Checkbox
                                checked={draft.isSick}
                                onChange={(_event, isSick) =>
                                    onChange(prev => ({ ...prev, isSick }))
                                }
                                disabled={disabled}
                            />
                        </Field>
                    )}
                </EditorFields>
            </FieldsViewport>
            <ScrollHint visible={hasMoreBelow}>
                Scrolla för fler fält
            </ScrollHint>
        </EditorBody>
    );
}
